/**
 * Utility functions for handling fullscreen across different browsers and devices
 * Supports iOS, Android, and desktop browsers
 *
 * USAGE:
 * - When opening Cab Control, call enterFullscreen() to enter fullscreen mode
 * - When closing Cab Control, call exitFullscreen() to exit fullscreen mode
 * - Listen for fullscreen changes with addFullscreenChangeListener() to handle
 *   manual exits (e.g., user pressing ESC key)
 *
 * BROWSER COMPATIBILITY:
 * - Chrome/Edge: Uses standard Fullscreen API
 * - Safari (desktop/iOS): Uses webkit-prefixed API
 * - Firefox: Uses moz-prefixed API
 * - IE11: Uses ms-prefixed API
 *
 * MOBILE CONSIDERATIONS:
 * - iOS Safari: Limited fullscreen support, but handles viewport expansion
 * - Android Chrome: Full support for Fullscreen API
 * - PWA mode: Enhanced fullscreen capabilities on both platforms
 *
 * NOTES:
 * - Fullscreen must be triggered by a user interaction (e.g., button click)
 * - Some browsers may show a notification when entering fullscreen
 * - ESC key will exit fullscreen on most browsers
 */

/**
 * Request fullscreen mode for a given element
 * @param element - The element to make fullscreen (defaults to document.documentElement)
 * @returns Promise that resolves when fullscreen is entered or rejects on error
 */
export const enterFullscreen = async (
  element: HTMLElement = document.documentElement,
): Promise<void> => {
  try {
    // Check for different browser prefixes
    if (element.requestFullscreen) {
      await element.requestFullscreen();
    } else if ((element as any).webkitRequestFullscreen) {
      // Safari and older Chrome
      await (element as any).webkitRequestFullscreen();
    } else if ((element as any).mozRequestFullScreen) {
      // Firefox
      await (element as any).mozRequestFullScreen();
    } else if ((element as any).msRequestFullscreen) {
      // IE11
      await (element as any).msRequestFullscreen();
    } else if ((element as any).webkitEnterFullscreen) {
      // iOS Safari for video elements
      await (element as any).webkitEnterFullscreen();
    } else {
      console.warn("Fullscreen API is not supported on this device/browser");
    }
  } catch (error) {
    console.error("Error entering fullscreen:", error);
    throw error;
  }
};

/**
 * Exit fullscreen mode
 * @returns Promise that resolves when fullscreen is exited or rejects on error
 */
export const exitFullscreen = async (): Promise<void> => {
  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen();
    } else if ((document as any).webkitExitFullscreen) {
      // Safari and older Chrome
      await (document as any).webkitExitFullscreen();
    } else if ((document as any).mozCancelFullScreen) {
      // Firefox
      await (document as any).mozCancelFullScreen();
    } else if ((document as any).msExitFullscreen) {
      // IE11
      await (document as any).msExitFullscreen();
    } else if ((document as any).webkitCancelFullScreen) {
      // iOS Safari
      await (document as any).webkitCancelFullScreen();
    } else {
      console.warn("Fullscreen API is not supported on this device/browser");
    }
  } catch (error) {
    console.error("Error exiting fullscreen:", error);
    throw error;
  }
};

/**
 * Check if currently in fullscreen mode
 * @returns true if in fullscreen, false otherwise
 */
export const isFullscreen = (): boolean => {
  return !!(
    document.fullscreenElement ||
    (document as any).webkitFullscreenElement ||
    (document as any).mozFullScreenElement ||
    (document as any).msFullscreenElement ||
    (document as any).webkitCurrentFullScreenElement
  );
};

/**
 * Toggle fullscreen mode
 * @param element - The element to make fullscreen (defaults to document.documentElement)
 * @returns Promise that resolves when toggle is complete
 */
export const toggleFullscreen = async (
  element: HTMLElement = document.documentElement,
): Promise<void> => {
  if (isFullscreen()) {
    await exitFullscreen();
  } else {
    await enterFullscreen(element);
  }
};

/**
 * Check if fullscreen is supported on this device/browser
 * @returns true if fullscreen is supported, false otherwise
 */
export const isFullscreenSupported = (): boolean => {
  return !!(
    document.documentElement.requestFullscreen ||
    (document.documentElement as any).webkitRequestFullscreen ||
    (document.documentElement as any).mozRequestFullScreen ||
    (document.documentElement as any).msRequestFullscreen
  );
};

/**
 * Add a listener for fullscreen change events
 * @param callback - Function to call when fullscreen state changes
 * @returns Function to remove the event listener
 */
export const addFullscreenChangeListener = (callback: () => void): (() => void) => {
  const events = [
    "fullscreenchange",
    "webkitfullscreenchange",
    "mozfullscreenchange",
    "MSFullscreenChange",
  ];

  events.forEach((event) => {
    document.addEventListener(event, callback);
  });

  // Return cleanup function
  return () => {
    events.forEach((event) => {
      document.removeEventListener(event, callback);
    });
  };
};
